import { Button, Modal } from 'antd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  CloseOutlined
} from '@ant-design/icons';

// Bulk action bar — appears as a slim brand-tinted strip between the
// toolbar and the table whenever one or more rows are selected. The
// design language matches Linear / Notion's multi-select strips: a
// quiet panel with a count + dismissible × on the left and the
// available bulk actions on the right.
//
// Built-in actions:
//   - "Delete" when `actions.delete` is enabled and `onBulkDelete` is
//     provided. Always behind a confirmation Modal.
//
// Custom bulk actions — `bulkActions` is an array of:
//   {
//     key,                              // unique React key
//     label,                            // button label
//     icon?,                            // antd icon element
//     danger?,                          // bool — render in danger style
//     requiresConfirmation?,            // bool | string — modal confirm copy
//     // Canonical handler shape (matches the spec + useErpMaster):
//     handler?: (records, ids) => Promise<void> | void,
//     // Legacy alias kept for back-compat:
//     onClick?: (ids, records) => void
//   }
//
// useErpMaster auto-wraps any action whose `requiresApprovalMfa` is
// set so the step-up flow runs before the handler — the handler
// itself doesn't change shape.

export default function ErpDataGridBulkBar({
  selectedRowKeys = [],
  selectedRecords = [],
  onClearSelection,
  bulkActions = [],
  onBulkDelete,
  canDelete = false
}) {
  if (!selectedRowKeys || selectedRowKeys.length === 0) return null;

  const count = selectedRowKeys.length;

  const confirmBulkDelete = () => {
    Modal.confirm({
      title: `Delete ${count} ${count === 1 ? 'record' : 'records'}?`,
      icon: <ExclamationCircleOutlined className="!text-rose-500" />,
      content:
        'This action cannot be undone. The selected records will be removed.',
      okText: `Delete ${count}`,
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      maskClosable: true,
      autoFocusButton: 'cancel',
      onOk: async () => {
        if (typeof onBulkDelete === 'function') {
          await onBulkDelete(selectedRowKeys, selectedRecords);
        }
      }
    });
  };

  return (
    <div className="erp-grid__bulk-bar" role="region" aria-label="Bulk actions">
      <div className="erp-grid__bulk-bar-left">
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined style={{ fontSize: 11 }} />}
          aria-label="Clear selection"
          onClick={onClearSelection}
          className="erp-grid__bulk-bar-dismiss"
        />
        <span className="erp-grid__bulk-bar-count">
          <span className="erp-grid__bulk-bar-count-num">{count.toLocaleString()}</span>
          <span className="erp-grid__bulk-bar-count-text">
            {count === 1 ? 'record selected' : 'records selected'}
          </span>
        </span>
      </div>

      <div className="erp-grid__bulk-bar-actions">
        {bulkActions.map((action) => {
          // Prefer the canonical `handler(records, ids)` shape (spec
          // + useErpMaster). Fall back to legacy `onClick(ids, records)`
          // for old call-sites — we pass arguments in each function's
          // own documented order so neither shape is broken.
          const useHandler = typeof action.handler === 'function';
          const useOnClick = !useHandler && typeof action.onClick === 'function';
          if (!useHandler && !useOnClick) return null;
          const fire = () => {
            if (useHandler) return action.handler(selectedRecords, selectedRowKeys);
            return action.onClick(selectedRowKeys, selectedRecords);
          };
          const onPress = () => {
            if (action.requiresConfirmation) {
              const copy =
                typeof action.requiresConfirmation === 'string'
                  ? action.requiresConfirmation
                  : `${action.label}?`;
              Modal.confirm({
                title: copy,
                icon: <ExclamationCircleOutlined className="!text-rose-500" />,
                content: `This will apply to ${count} ${
                  count === 1 ? 'record' : 'records'
                }.`,
                okText: action.label || 'Confirm',
                okButtonProps: { danger: Boolean(action.danger) },
                cancelText: 'Cancel',
                maskClosable: true,
                autoFocusButton: 'cancel',
                onOk: async () => {
                  await fire();
                }
              });
              return;
            }
            fire();
          };
          return (
            <Button
              key={action.key}
              size="small"
              icon={action.icon}
              danger={action.danger}
              onClick={onPress}
              className="erp-grid__bulk-bar-btn"
            >
              {action.label}
            </Button>
          );
        })}

        {canDelete && typeof onBulkDelete === 'function' ? (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={confirmBulkDelete}
            className="erp-grid__bulk-bar-btn"
          >
            Delete
          </Button>
        ) : null}
      </div>
    </div>
  );
}
